import dayjs from "dayjs";

import { ISearchPeopleFilter } from "~/interfaces";
import { getGoogleCalendarEvents } from "./google-calendar";
import { DAYJS_FORMAT } from "~/const-files/const";
import { getBirthdayCalendarPeople } from "~/persons/persons";
import { CUSTOM_DATE_EVENTS } from "~/const-files/custom-date-events";

interface IBirthdateItem {
	person: string;
	age: number;
	type: "birthdate";
}

interface IHolidateItem {
	person: string;
	type: "holidate";
}

interface ICalendarItem {
	text: string;
	type: "calendar";
}

type TAllItems = IBirthdateItem | IHolidateItem | ICalendarItem;

interface IDateItem {
	date: number;
	month: number;
	diffMonth: boolean;
	active: boolean;
	past: boolean;
	items: Array<TAllItems>;
}

const YEAR_LEN = 4;
const YEAR_MONTH_LEN = 7;
const PERCENT = 100;
const DIFF_DAYS = 3;
const WEEK_DAYS = 6;
// okruh osob jako v horni liste (getInfoLine)
const INFO_LINE_FILTER: ISearchPeopleFilter = {
	service: "makuderovi",
	perms: 0,
};

function insertToArray(sourceArray: Array<IDateItem>, dateData: Omit<IDateItem, "items">, insItem: TAllItems) {
	const filtered = sourceArray.filter(item => item.date === dateData.date);

	if (filtered.length === 1) {
		filtered[0].items.push(insItem);
	} else {
		sourceArray.push({
			...dateData,
			items: [insItem],
		});
	}
}

function getCustomEvents(activeDays: Array<IDateItem>, fromDate = "", toDate = "") {
	if (fromDate === "" || toDate === "") {
		return;
	}

	const fromDateObj = dayjs(fromDate);
	const toDateObj = dayjs(toDate);
	const fromDateUnix = fromDateObj.unix();
	const toDateUnix = toDateObj.unix();
	const diffMonth = fromDateObj && toDateObj && fromDateObj.get("month") !== toDateObj.get("month");
	const curDate = dayjs();

	// google kalendar
	const googleCalendarEvents = getGoogleCalendarEvents();

	googleCalendarEvents.forEach(calendarEvent => {
		const eventDate = dayjs(dayjs(calendarEvent.startDate).format(DAYJS_FORMAT));
		const eventDateUnix = eventDate.unix();

		if (eventDateUnix >= fromDateUnix && eventDateUnix <= toDateUnix) {
			insertToArray(activeDays, {
				date: eventDate.get("date"),
				month: eventDate.get("month"),
				active: curDate.get("date") === eventDate.get("date") && curDate.get("month") === eventDate.get("month"),
				past: curDate.get("month") === eventDate.get("month") && eventDate.get("date") < curDate.get("date"),
				diffMonth,
			}, {
				text: calendarEvent.summary,
				type: "calendar",
			});
		}
	});

	// vlastni udalosti
	for (const customEvent of CUSTOM_DATE_EVENTS) {
		const eventDate = dayjs(customEvent.date);
		const eventDateUnix = eventDate.unix();

		if (eventDateUnix >= fromDateUnix && eventDateUnix <= toDateUnix) {
			insertToArray(activeDays, {
				date: eventDate.get("date"),
				month: eventDate.get("month"),
				active: curDate.get("date") === eventDate.get("date") && curDate.get("month") === eventDate.get("month"),
				past: curDate.get("month") === eventDate.get("month") && eventDate.get("date") < curDate.get("date"),
				diffMonth,
			}, {
				text: customEvent.title,
				type: "calendar",
			});
		}
	}
}

function getMonthEvents({
	filter,
	curDate,
	fromDate = "",
	toDate = "",
	calendar = false,
}: {
	filter: ISearchPeopleFilter;
	curDate?: dayjs.Dayjs;
	fromDate?: string;
	toDate?: string;
	calendar?: boolean;
}) {
	const items: Array<IDateItem> = [];
	const fromDateObj = fromDate === ""
		? null
		: dayjs(fromDate);
	const toDateObj = toDate === ""
		? null
		: dayjs(toDate);
	// lide
	const persons = getBirthdayCalendarPeople(filter);
	const diffMonth = fromDateObj && toDateObj && fromDateObj.get("month") !== toDateObj.get("month");

	persons.forEach(person => {
		// preskocime zesnule
		if ("deceased" in person) {
			return;
		}

		const birthdate = person.birthdate
			? dayjs(person.birthdate)
			: null;
		const holiday = person.holiday
			? dayjs(`${dayjs().get("year")}-${person.holiday}`)
			: null;

		// cely format datumu narozeni
		// spatne - chceme porovnat na 10, ne více!
		if (birthdate && person.birthdate.length > YEAR_LEN) {
			if (curDate.get("month") === birthdate.get("month")) {
				const curYearBirthdayObj = birthdate.clone().set("year", dayjs().get("year"));
				const curYearBirthday = curYearBirthdayObj.unix();

				if ((!fromDateObj && !toDateObj) || (fromDateObj && toDateObj && curYearBirthday >= fromDateObj.unix() && curYearBirthday <= toDateObj.unix())) {
					insertToArray(items, {
						date: birthdate.get("date"),
						month: curDate.get("month"),
						active: curDate.get("date") === birthdate.get("date") && curDate.get("month") === birthdate.get("month"),
						past: curDate.get("month") === birthdate.get("month") && birthdate.get("date") < curDate.get("date"),
						diffMonth,
					}, {
						person: person.fullName,
						age: person.birthdate
							? curDate.year() - dayjs(person.birthdate).year()
							: 0,
						type: "birthdate",
					});
				}
			}
		}

		if (holiday) {
			if (curDate.get("month") === holiday.get("month")) {
				if ((!fromDateObj && !toDateObj) || (fromDateObj && toDateObj && holiday.unix() >= fromDateObj.unix() && holiday.unix() <= toDateObj.unix())) {
					insertToArray(items, {
						date: holiday.get("date"),
						month: curDate.get("month"),
						active: curDate.get("date") === holiday.get("date") && curDate.get("month") === holiday.get("month"),
						past: curDate.get("month") === holiday.get("month") && holiday.get("date") < curDate.get("date"),
						diffMonth,
					}, {
						person: person.fullName,
						type: "holidate",
					});
				}
			}
		}
	});

	calendar && getCustomEvents(items, fromDate, toDate);
	items.sort((aItem, bItem) => (aItem.date + (aItem.month * PERCENT)) - (bItem.date + (bItem.month * PERCENT)));

	return items;
}

function formatItems(items: Array<IDateItem>, diffDays = 0) {
	const output: Array<string> = [];

	items.forEach(item => {
		const curDate = dayjs().get("date");
		const fromDate = curDate - diffDays;
		const toDate = curDate + diffDays;

		if (diffDays === 0 || (item.date >= fromDate && item.date <= toDate)) {
			const allItems: Array<string> = [];

			item.items.forEach(subItem => {
				let itemText = "";

				switch (subItem.type) {
					case "birthdate":
						// v den narozeni misto "nar. 0"
						itemText = subItem.age === 0
							? `${subItem.person} - narození`
							: `${subItem.person} - nar. ${subItem.age}`;
						break;

					case "calendar":
						itemText = "";
						break;

					case "holidate":
						itemText = `${subItem.person} - svát.`;
						break;

					default:
						itemText = "";
				}

				if (itemText) {
					allItems.push(itemText);
				}
			});

			if (allItems.length) {
				output.push(`${item.date}. ${allItems.join(", ")}`);
			}
		}
	});

	return output;
}

export function getMonthsData({
	filter,
	curDate = dayjs(),
	diffDays = DIFF_DAYS,
}: {
	filter: ISearchPeopleFilter;
	curDate: dayjs.Dayjs;
	diffDays?: number;
}) {
	const prevData = getMonthEvents({
		curDate: curDate.clone().set("date", 1).subtract(1, "month"),
		filter,
	});
	const curData = getMonthEvents({
		curDate,
		filter,
	});
	const nextData = getMonthEvents({
		curDate: curDate.clone().set("date", 1).add(1, "month"),
		filter,
	});

	return {
		prev: formatItems(prevData),
		cur: formatItems(curData),
		next: formatItems(nextData),
		active: dayjs().format("YYYY-MM") === curDate.format("YYYY-MM")
			? formatItems(curData, diffDays)
			: [],
	};
}

export function getCurrentWeekData(monday: dayjs.Dayjs) {
	const curData = getMonthEvents({
		curDate: dayjs(),
		fromDate: monday.format(DAYJS_FORMAT),
		toDate: monday.add(WEEK_DAYS, "days").format(DAYJS_FORMAT),
		calendar: true,
		filter: INFO_LINE_FILTER,
	});

	return curData;
}

export type TDayPeopleItem = IBirthdateItem | IHolidateItem;

// osoba je k danemu dni uz narozena; u neuplneho data (jen rok / rok-mesic) az po skonceni celeho obdobi
function isBornAt(birthdate: string | undefined, date: dayjs.Dayjs) {
	if (!birthdate) {
		return true;
	}

	const birthdateObj = dayjs(birthdate);

	if (birthdate.length === YEAR_LEN) {
		return date.isAfter(birthdateObj, "year");
	}

	if (birthdate.length === YEAR_MONTH_LEN) {
		return date.isAfter(birthdateObj, "month");
	}

	return !date.isBefore(birthdateObj, "day");
}

// narozeniny a svatky osob z horni listy pro konkretni den (vek k roku daneho dne)
export function getDayPeopleData(date: dayjs.Dayjs) {
	const birthdates: Array<IBirthdateItem> = [];
	const holidates: Array<IHolidateItem> = [];
	const dayMonth = date.format("MM-DD");
	const persons = getBirthdayCalendarPeople(INFO_LINE_FILTER);

	persons.forEach(person => {
		if ("deceased" in person) {
			return;
		}

		if (person.birthdate && person.birthdate.length > YEAR_LEN) {
			const birthdate = dayjs(person.birthdate);
			const age = date.year() - birthdate.year();

			if (birthdate.format("MM-DD") === dayMonth && age >= 0) {
				birthdates.push({
					person: person.fullName,
					age,
					type: "birthdate",
				});
			}
		}

		if (person.holiday === dayMonth && isBornAt(person.birthdate, date)) {
			holidates.push({
				person: person.fullName,
				type: "holidate",
			});
		}
	});

	const items: Array<TDayPeopleItem> = [...birthdates, ...holidates];

	return items;
}
